import { apiCall } from "api/axios";
import { IGetBusinessAccountsPayload } from "contexts/businesses-context/types";
import { getTodayAsEndDateFilterValue } from "utils";

import {
  IActivateOrDeactivateODPRollOverConfigPayload,
  IActivateOrDeactivateODPRollOverConfigResponseData,
  IApiGetBusinessYieldPayload,
  IApproveOrRejectBusinessTransactionLimitUpgradePayload,
  IApproveOrRejectBusinessTransactionLimitUpgradeResponseData,
  IBusinessAccountTransactionsPayload,
  IBusinessAccountTransactionsResponseData,
  IBusinessAccountTransactionsStatsResponseData,
  IBusinessAccountsResponseData,
  IBusinessActivateOrDeactivatePayload,
  IBusinessActivateOrDeactivateResponseData,
  IBusinessAdminsResponseData,
  IBusinessDetailsResponseData,
  IBusinessEmployeesResponseData,
  IBusinessListPayload,
  IBusinessODPConfigUpdatePayload,
  IBusinessODPResponseData,
  IBusinessODPUpdateResponseData,
  IBusinessPayrollsResponseData,
  IBusinessProfileCreatedResponseData,
  IBusinessStatsResponseData,
  IBusinessStatusUpdatePayload,
  IBusinessStatusUpdateResponseData,
  IBusinessTransactionLimitSettingsResponseData,
  IBusinessTransactionResponseData,
  IBusinessTransactionsPayload,
  IBusinessYieldResponseData, // IBusinessesExportPayload,
  // IBusinessesExportResponseData,
  IBusinessesListResponseData,
  ICloseSubAccountPayload,
  ICloseSubAccountResponseData,
  ICreateBusinessDirectorsPayload,
  ICreateBusinessDirectorsResponseData,
  ICreateEmailNotificationPreferencesResponseData,
  ICreateEmailNotificationsPreferencesPayload,
  ICreateNewBusinessProfilePayload,
  IDeleteEmailNotificationPreferencePayload,
  IEditEmailNotificationPreferencesResponseData,
  IEditEmailNotificationsPreferencesPayload,
  IExportAccountTransactionsPayload,
  IExportBusinessTransactionsPayload,
  IExportPayrollsPayload,
  IExportResponseData,
  IExportYieldPayload,
  IFreezeBusinessTotallyPayload,
  IFreezeBusinessTotallyResponseData,
  IFreezeOrUnfreezeSubAccountPayload,
  IFreezeOrUnfreezeSubAccountResponseData,
  IGenerateAccountStatementPayload,
  IGenerateAccountStatementResponseData,
  IGetBusinessAdminsPayload,
  IGetBusinessDetailsPayload,
  IGetBusinessEmployeesPayload,
  IGetBusinessEmployeesWithOdpRestrictionsPayload,
  IGetBusinessEmployeesWithOdpRestrictionsResponseData,
  IGetBusinessPayrollsPayload,
  IGetBusinessStatsPayload,
  IGetBusinessTransactionLimitSettingsPayload,
  IGetDefaultBusinessTransactionLimitPayload,
  IGetDefaultBusinessTransactionLimitResponseData,
  IGetEmailNotificationsPreferenceResponseData,
  IPauseOrResumeEmployeeWithdrawalsPayload,
  IPauseOrResumeEmployeeWithdrawalsResponseData,
  IRegistrationNumVerificationResponseData,
  IResetBusinessAdminsPasswordPayload,
  IResetBusinessAdminsPasswordResponseData,
  IUpdateBusinessDetailsPayload,
  IUpdateBusinessDetailsResponseData,
  IUpdateBusinessProfilePayload,
  IUpdateBusinessProfileResponseData,
  IUpgradeBusinessTransactionLimitPayload,
  IUpgradeBusinessTransactionLimitResponseData,
  IVerifyRegistrationNumPayload,
} from "./types";

export const getBusinessesStats = async (payload: IGetBusinessStatsPayload) => {
  const payloadData = { ...payload };
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  const result = await apiCall({
    apiRoute: `/control/business/compliance/business-stats${queryString ? `?${queryString}` : ""}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IBusinessStatsResponseData;
};

export const getBusinessesData = async (payload: IBusinessListPayload) => {
  delete payload.isRefetching;
  const newActiveField = payload.isActive;
  delete payload.isActive;
  const payloadData = { ...payload };
  if (newActiveField !== undefined) {
    payloadData["active"] = newActiveField;
  }
  if (payloadData.startDate && !payloadData.endDate) {
    payloadData.endDate = getTodayAsEndDateFilterValue();
  }
  if (payload?.deactivated !== undefined) {
    payloadData["isActive"] = !payload.deactivated;
  }
  const queryString = Object.entries(payloadData)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  const result = await apiCall({
    apiRoute: `/control/business/compliance/businesses?${queryString}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IBusinessesListResponseData;
};

// export const exportBusinesses = async (payload: IBusinessesExportPayload) => {
//   const data = { ...payload };
//   if (data.startDate && !data.endDate) {
//     data["endDate"] = getTodayAsEndDateFilterValue();
//   }
//   const result = await apiCall({
//     apiRoute: "/control/business/compliance/business-export",
//     module: "EARNIPAY_API",
//     config: {
//       method: "POST",
//       data,
//     },
//     useEncryption: true,
//   });
//   return result as IBusinessesExportResponseData;
// };

export const getBusinessDetails = async (
  payload: IGetBusinessDetailsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessDetailsResponseData;
};

export const createNewBusinessProfile = async (
  payload: ICreateNewBusinessProfilePayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/create-business",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessProfileCreatedResponseData;
};

export const verifyRegistrationNumber = async (
  payload: IVerifyRegistrationNumPayload
) => {
  const result = await apiCall({
    apiRoute: `/control/business/compliance/verify-registration-number`,
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IRegistrationNumVerificationResponseData;
};

export const updateBusinessProfile = async (
  payload: Partial<IUpdateBusinessProfilePayload>
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/update-company",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IUpdateBusinessProfileResponseData;
};

export const createBusinessDirectors = async (
  payload: ICreateBusinessDirectorsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/create-business-director",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as ICreateBusinessDirectorsResponseData;
};

export const updateBusinessDirectors = async (
  payload: ICreateBusinessDirectorsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/update-business-director",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as ICreateBusinessDirectorsResponseData;
};

export const updateBusinessStatus = async (
  payload: IBusinessStatusUpdatePayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/update-business",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessStatusUpdateResponseData;
};

export const getBusinessTransactions = async (
  payload: IBusinessTransactionsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/business/transactions",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessTransactionResponseData;
};

export const getBusinessAccounts = async (
  payload: IGetBusinessAccountsPayload
) => {
  delete payload.isRefetching;
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/accounts",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IBusinessAccountsResponseData;
};

export const getBusinessAccountTransactionsStats = async (walletId: string) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/account/stat",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: { walletId: +walletId },
    },
    useEncryption: true,
  });
  return result as IBusinessAccountTransactionsStatsResponseData;
};

export const getBusinessAccountTransactions = async (
  payload: IBusinessAccountTransactionsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }

  const result = await apiCall({
    apiRoute: "/control/business/compliance/account/transactions",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessAccountTransactionsResponseData;
};

export const freezeOrUnfreezeSubAccount = async (
  payload: IFreezeOrUnfreezeSubAccountPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/sub-account/freeze",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IFreezeOrUnfreezeSubAccountResponseData;
};

export const closeSubAccount = async (payload: ICloseSubAccountPayload) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/sub-account/close",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as ICloseSubAccountResponseData;
};

export const generateAccountStatement = async (
  payload: IGenerateAccountStatementPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/generate-statement",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IGenerateAccountStatementResponseData;
};

export const freezeBusinessTotally = async (
  payload: IFreezeBusinessTotallyPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/accounts/freeze",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IFreezeBusinessTotallyResponseData;
};

export const activateOrDeactivateBusiness = async (
  payload: IBusinessActivateOrDeactivatePayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/activate-deactivate",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessActivateOrDeactivateResponseData;
};

export const getBusinessAdmins = async (payload: IGetBusinessAdminsPayload) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/admins",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessAdminsResponseData;
};

export const getBusinessEmployees = async (
  payload: IGetBusinessEmployeesPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/employees",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessEmployeesResponseData;
};

export const getBusinessODPConfig = async (businessId: string) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/odp-configuration",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        businessId,
      },
    },
    useEncryption: true,
  });
  return result as IBusinessODPResponseData;
};

export const editBusinessODPConfig = async (
  payload: Partial<IBusinessODPConfigUpdatePayload>
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/odp-configuration",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessODPUpdateResponseData;
};

export const activateOrDeactivateODPRollOverConfig = async (
  payload: IActivateOrDeactivateODPRollOverConfigPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/activate-deactivate-rollover",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IActivateOrDeactivateODPRollOverConfigResponseData;
};

export const pauseOrResumeEmployeeWithdrawals = async (
  payload: IPauseOrResumeEmployeeWithdrawalsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/pause-resume-employees-withdrawal",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IPauseOrResumeEmployeeWithdrawalsResponseData;
};

export const getBusinessYield = async (
  payload: IApiGetBusinessYieldPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/yield",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessYieldResponseData;
};

export const getBusinessPayrolls = async (
  payload: IGetBusinessPayrollsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }

  const result = await apiCall({
    apiRoute: "/control/business/compliance/view-payroll",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IBusinessPayrollsResponseData;
};

export const getBusinessTransactionLimitSettings = async (
  payload: IGetBusinessTransactionLimitSettingsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/transaction-limit",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IBusinessTransactionLimitSettingsResponseData;
};

export const upgradeBusinessTransactionLimit = async (
  payload: IUpgradeBusinessTransactionLimitPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/upgrade-limit",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IUpgradeBusinessTransactionLimitResponseData;
};

export const approveOrRejectBusinessTransactionLimitUpgrade = async (
  payload: IApproveOrRejectBusinessTransactionLimitUpgradePayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/transaction-limit",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IApproveOrRejectBusinessTransactionLimitUpgradeResponseData;
};

export const getBusinessDefaultTransactionLimit = async (
  payload: IGetDefaultBusinessTransactionLimitPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/default-limit",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IGetDefaultBusinessTransactionLimitResponseData;
};

export const resetBusinessAdminsPassword = async (
  payload: IResetBusinessAdminsPasswordPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/reset-password",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IResetBusinessAdminsPasswordResponseData;
};

export const exportBusinessTransactions = async (
  payload: IExportBusinessTransactionsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/business-transaction-export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const exportAccountTransactions = async (
  payload: IExportAccountTransactionsPayload
) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/account-transaction-export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const exportYieldData = async (payload: IExportYieldPayload) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/yield-export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const exportPayrollsData = async (payload: IExportPayrollsPayload) => {
  const data = { ...payload };
  if (data.startDate && !data.endDate) {
    data["endDate"] = getTodayAsEndDateFilterValue();
  }
  const result = await apiCall({
    apiRoute: "/control/business/compliance/payroll-export",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as IExportResponseData;
};

export const updateBusinessDetails = async (
  payload: IUpdateBusinessDetailsPayload
) => {
  const result = await apiCall({
    apiRoute: "/control/business/compliance/update-business-detail",
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data: payload,
    },
    useEncryption: true,
  });
  return result as IUpdateBusinessDetailsResponseData;
};

export const getBusinessEmployeesWithOdpRestrictions = async (
  payload: IGetBusinessEmployeesWithOdpRestrictionsPayload
) => {
  const result = await apiCall({
    apiRoute:
      "/control/business/compliance/employees-with-odp-withdrawal-restrictions",
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data: {
        ...payload,
        page: String(payload.page),
        limit: String(payload.limit),
      },
    },
    useEncryption: true,
  });
  return result as IGetBusinessEmployeesWithOdpRestrictionsResponseData;
};

export const getEmailNotificationPreferences = async (businessId: string) => {
  const result = await apiCall({
    apiRoute: `/b2b/employer/setting/company/email-notification-preference/${businessId}`,
    module: "EARNIPAY_API",
    config: {
      method: "GET",
    },
    useEncryption: true,
  });
  return result as IGetEmailNotificationsPreferenceResponseData;
};

export const createNewEmailNotificationPreferences = async (
  payload: ICreateEmailNotificationsPreferencesPayload
) => {
  const { businessId, ...data } = payload;
  const result = await apiCall({
    apiRoute: `/b2b/employer/setting/company/email-notification-preference/${businessId}`,
    module: "EARNIPAY_API",
    config: {
      method: "POST",
      data,
    },
    useEncryption: true,
  });
  return result as ICreateEmailNotificationPreferencesResponseData;
};

export const editEmailNotificationPreferences = async (
  payload: IEditEmailNotificationsPreferencesPayload
) => {
  const { businessId, ...data } = payload;
  const result = await apiCall({
    apiRoute: `/b2b/employer/setting/company/email-notification-preference/${businessId}`,
    module: "EARNIPAY_API",
    config: {
      method: "PATCH",
      data,
    },
    useEncryption: true,
  });
  return result as IEditEmailNotificationPreferencesResponseData;
};

export const deleteEmailNotificationPreference = async (
  payload: IDeleteEmailNotificationPreferencePayload
) => {
  const { businessId, emailNotificationId } = payload;
  const result = await apiCall({
    apiRoute: `/b2b/employer/setting/company/email-notification-preference/${businessId}/${emailNotificationId}`,
    module: "EARNIPAY_API",
    config: {
      method: "DELETE",
    },
    useEncryption: true,
  });
  return result as IEditEmailNotificationPreferencesResponseData;
};
